import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, CircleAlert, Loader2, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames } from '@/lib/utils';
import { canalDe, type Campana, type CampanaEditable } from '@/lib/comunicaciones';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import PasoDestinatarios from '@/components/comunicaciones/PasoDestinatarios';
import PasoContenido from '@/components/comunicaciones/PasoContenido';
import PasoContenidoWhatsApp from '@/components/comunicaciones/PasoContenidoWhatsApp';
import PasoEnviar from '@/components/comunicaciones/PasoEnviar';
import InformeCampana from '@/components/comunicaciones/InformeCampana';

type Paso = 'destinatarios' | 'contenido' | 'enviar';
/** `corto` es el del móvil: los tres largos no caben en 375 px. */
const PASOS: { id: Paso; texto: string; corto: string }[] = [
  { id: 'destinatarios', texto: '1. Destinatarios', corto: '1. A quién' },
  { id: 'contenido', texto: '2. Contenido', corto: '2. Contenido' },
  { id: 'enviar', texto: '3. Revisar y enviar', corto: '3. Enviar' },
];

type EstadoGuardado = 'guardado' | 'pendiente' | 'guardando' | 'error';

function editable(c: Campana): CampanaEditable {
  return {
    nombre: c.nombre, audiencia: c.audiencia, remitente: c.remitente, idiomaReserva: c.idiomaReserva, versiones: c.versiones,
    ...(c.whatsapp ? { whatsapp: c.whatsapp } : {}),
  };
}

/**
 * Una campaña: el editor en tres pasos si es un borrador, el informe si no.
 *
 * El borrador se guarda solo, un segundo después del último cambio. Antes de
 * revisar, probar o enviar se fuerza el guardado (`guardarYa`), para que el
 * servidor trabaje siempre con lo que se ve en pantalla.
 */
export default function CampanaPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [campana, setCampana] = useState<Campana | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paso, setPaso] = useState<Paso>('destinatarios');
  const [guardado, setGuardado] = useState<EstadoGuardado>('guardado');
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null);
  const ajustes = useApi(() => api.getAjustesComunicaciones(), []);

  const pendiente = useRef<CampanaEditable | null>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout>>();
  const enVuelo = useRef<Promise<boolean> | null>(null);

  const cargar = useCallback(async () => {
    try {
      setCampana(await api.getCampana(id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar');
    }
  }, [id]);
  useEffect(() => { void cargar(); }, [cargar]);

  /** Manda lo pendiente. Si ya hay un guardado en vuelo, espera a ese y repite. */
  const guardarYa = useCallback(async (): Promise<boolean> => {
    clearTimeout(temporizador.current);
    if (enVuelo.current) await enVuelo.current;
    const datos = pendiente.current;
    if (!datos) return true;
    pendiente.current = null;
    setGuardado('guardando');
    const promesa = api.guardarCampana(id, datos)
      .then(() => {
        setGuardado(pendiente.current ? 'pendiente' : 'guardado');
        setErrorGuardado(null);
        return true;
      })
      .catch(err => {
        // Se vuelve a dejar pendiente para el siguiente intento, salvo que ya haya algo más nuevo
        pendiente.current = pendiente.current ?? datos;
        setGuardado('error');
        setErrorGuardado(err instanceof Error ? err.message : 'No se pudo guardar');
        return false;
      })
      .finally(() => { enVuelo.current = null; });
    enVuelo.current = promesa;
    return promesa;
  }, [id]);

  const cambiar = useCallback((cambios: Partial<CampanaEditable>) => {
    setCampana(c => {
      if (!c) return c;
      const nueva = { ...c, ...cambios };
      pendiente.current = editable(nueva);
      return nueva;
    });
    setGuardado('pendiente');
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => { void guardarYa(); }, 1000);
  }, [guardarYa]);

  // Avisar antes de cerrar la pestaña con cambios sin guardar
  useEffect(() => {
    const aviso = (e: BeforeUnloadEvent) => {
      if (pendiente.current || enVuelo.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', aviso);
    return () => {
      window.removeEventListener('beforeunload', aviso);
      // Al salir dentro del CRM, lo pendiente se guarda igualmente
      if (pendiente.current) void guardarYa();
    };
  }, [guardarYa]);

  const irAPaso = async (p: Paso) => {
    await guardarYa();
    setPaso(p);
  };

  const borrar = async () => {
    if (!campana || !window.confirm(`¿Borrar el borrador «${campana.nombre}»?`)) return;
    clearTimeout(temporizador.current);
    pendiente.current = null;
    await api.borrarCampana(campana.id);
    navigate('/comunicaciones/campanas');
  };

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <Volver />
        <div className="card text-red-600 text-sm flex items-center gap-2"><CircleAlert size={16} /> {error}</div>
      </div>
    );
  }
  if (!campana) return <LoadingSpinner />;

  const borrador = campana.estado === 'borrador';

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <Volver />
      <div className="flex flex-wrap items-center gap-3">
        {borrador ? (
          <input
            className="text-xl sm:text-2xl font-bold bg-transparent border-b border-transparent hover:border-gray-200 focus:border-sereno-dark focus:outline-none flex-1 min-w-[200px] text-sereno-dark"
            value={campana.nombre}
            onChange={e => cambiar({ nombre: e.target.value })}
            onBlur={() => { if (!campana.nombre.trim()) cambiar({ nombre: 'Campaña sin nombre' }); }}
            aria-label="Nombre de la campaña (solo lo ves tú)"
            title="Nombre interno: no lo ven los destinatarios"
          />
        ) : (
          <h1 className="text-xl sm:text-2xl font-bold text-sereno-dark flex-1">{campana.nombre}</h1>
        )}
        {borrador && <IndicadorGuardado estado={guardado} error={errorGuardado} onReintentar={() => void guardarYa()} />}
        {borrador && (
          <button type="button" onClick={borrar} className="text-sereno-dark/40 hover:text-red-600 p-2" title="Borrar el borrador">
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {borrador ? (
        <>
          <div className="border-b border-slate-200 dark:border-white/10">
            <nav className="flex gap-1 -mb-px overflow-x-auto">
              {PASOS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => irAPaso(p.id)}
                  className={classNames(
                    'px-3 sm:px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors',
                    paso === p.id
                      ? 'border-sereno-dark text-sereno-dark font-medium dark:border-white dark:text-white'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
                  )}
                >
                  <span className="sm:hidden">{p.corto}</span>
                  <span className="hidden sm:inline">{p.texto}</span>
                </button>
              ))}
            </nav>
          </div>

          {paso === 'destinatarios' && <PasoDestinatarios audiencia={campana.audiencia} canal={canalDe(campana)} onChange={audiencia => cambiar({ audiencia })} />}
          {paso === 'contenido' && (canalDe(campana) === 'whatsapp'
            ? <PasoContenidoWhatsApp campana={campana} onChange={cambiar} />
            : <PasoContenido campana={campana} onChange={cambiar} />)}
          {paso === 'enviar' && (
            <PasoEnviar campana={campana} ajustes={ajustes.data} onChange={cambiar} guardarYa={guardarYa} onLanzada={() => void cargar()} />
          )}

          <div className="flex justify-between pt-2">
            {paso !== 'destinatarios'
              ? <button type="button" className="btn-ghost text-sm" onClick={() => irAPaso(paso === 'enviar' ? 'contenido' : 'destinatarios')}>← Anterior</button>
              : <span />}
            {paso !== 'enviar' && (
              <button type="button" className="btn-primary text-sm" onClick={() => irAPaso(paso === 'destinatarios' ? 'contenido' : 'enviar')}>Siguiente →</button>
            )}
          </div>
        </>
      ) : (
        <InformeCampana campana={campana} recargar={cargar} />
      )}
    </div>
  );
}

function Volver() {
  return (
    <Link to="/comunicaciones/campanas" className="inline-flex items-center gap-1 text-sm text-sereno-dark/50 hover:text-sereno-dark">
      <ArrowLeft size={14} /> Campañas
    </Link>
  );
}

function IndicadorGuardado({ estado, error, onReintentar }: { estado: EstadoGuardado; error: string | null; onReintentar: () => void }) {
  if (estado === 'error') {
    return (
      <button type="button" onClick={onReintentar} className="text-xs text-red-600 flex items-center gap-1" title={error || undefined}>
        <CircleAlert size={14} /> No se guardó: {error} · Reintentar
      </button>
    );
  }
  return (
    <span className="text-xs text-sereno-dark/40 flex items-center gap-1">
      {estado === 'guardado'
        ? <><CheckCircle2 size={14} /> Guardado</>
        : <><Loader2 size={14} className="animate-spin" /> Guardando…</>}
    </span>
  );
}
